import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

const MIRROR_PROPS = [
  'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize',
  'fontSizeAdjust', 'lineHeight', 'fontFamily', 'textAlign', 'textTransform',
  'textIndent', 'textDecoration', 'letterSpacing', 'wordSpacing', 'tabSize',
  'whiteSpace', 'wordWrap', 'wordBreak',
];

// Caret pixel position within a <textarea> via a hidden mirror div.
const getTextareaCaret = (textarea, position) => {
  const mirror = document.createElement('div');
  const style = mirror.style;
  const computed = window.getComputedStyle(textarea);
  style.position = 'absolute';
  style.visibility = 'hidden';
  style.top = '0';
  style.left = '0';
  MIRROR_PROPS.forEach((prop) => { style[prop] = computed[prop]; });
  style.whiteSpace = 'pre-wrap';
  style.wordWrap = 'break-word';
  style.overflow = 'hidden';
  style.width = textarea.clientWidth + 'px';
  style.height = 'auto';

  mirror.textContent = textarea.value.substring(0, position);
  const marker = document.createElement('span');
  marker.textContent = textarea.value.substring(position) || '.';
  mirror.appendChild(marker);

  document.body.appendChild(mirror);
  const mirrorRect = mirror.getBoundingClientRect();
  const markerRect = marker.getBoundingClientRect();
  const rect = textarea.getBoundingClientRect();
  const lineH = parseInt(computed.lineHeight, 10) || parseInt(computed.fontSize, 10) || 16;
  const coords = {
    // Viewport coords of the caret bottom.
    top: rect.top + (markerRect.top - mirrorRect.top) - textarea.scrollTop + lineH,
    left: rect.left + (markerRect.left - mirrorRect.left) - textarea.scrollLeft,
    height: lineH,
    outOfView: false,
  };
  const within = (markerRect.top - mirrorRect.top) - textarea.scrollTop;
  if (within < -lineH || within > rect.height) coords.outOfView = true;
  document.body.removeChild(mirror);
  return coords;
};

// Mirrors backend _format_ar_citation_display
export const buildRefLabel = (ref) => {
  const arId = ref && ref.ar_id;
  const authors = Array.isArray(ref && ref.authors) ? ref.authors : [];
  const year = (ref && ref.year) || '';
  let authorDisplay;
  if (authors.length === 0) {
    authorDisplay = 'Anonymous ' + arId;
  } else {
    const first = String(authors[0]);
    const lastName = first.includes(',')
      ? first.split(',')[0].trim()
      : (first.split(/\s+/)[0] || first).trim();
    authorDisplay = authors.length > 1 ? (lastName + ' et al.') : lastName;
  }
  if (year) authorDisplay = authorDisplay + ', ' + year;
  return authorDisplay;
};

// The chip's source token. Without it the backend has to guess the id back from the
// display label, which is lossy for refs. item.id is the doc_id/ar_id; item.ref carries
// an existing token through when a chip is re-serialised.
export const citationToken = (item) => (
  item.ref || (item.id == null
    ? ''
    : (item.kind === 'ref' ? '[ar_id_' : '[doc_id_') + item.id + ']')
);

export const buildCitationHTML = (item) => {
  const cls = item.kind === 'ref' ? 'ev-message-card7' : 'ev-message-card6';
  const token = citationToken(item);
  const ref = token ? ' data-ref="' + token.replace(/"/g, '&quot;') + '"' : '';
  return '<span class="' + cls + ' m-0 mx-1 ev-btn-xsmall ev-bold-reg"' + ref + '>' + item.label + '</span>';
};

// Given plain text and a caret offset, find an active "@query" behind it.
const findActiveMention = (value, caret) => {
  if (value == null || caret == null) return null;
  const upto = value.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;
  const query = upto.slice(at + 1);
  if (/\s/.test(query)) return null;
  if (query.length > 60) return null;
  return { start: at, query: query };
};

const DROPDOWN_W = 300;
const DROPDOWN_H = 250;
const CITATION_BADGE_SELECTOR = '.ev-message-card6, .ev-message-card7';

const EMPTY_FORM = {
  classification: 'Journal Article', authors: '', year: '', title: '',
  journal_page_no: '', publisher: '', organization: '', doi: '',
  website_url: '', accessed_date: '',
};
const REF_TYPES = ['Journal Article', 'Book', 'Book Section', 'Report', 'Conference Paper', 'Web Page', 'Thesis', 'Other'];

const MentionAutocomplete = ({
  textareaRef, containerRef, selector, contentEditable = false,
  docs = [], refs = [], onInsert, onNeedReferences, refsLoading = false,
  onCreateReference,
  onSelectItem,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOverride, setSearchOverride] = useState(null); // typed in the box
  const [mentionMode, setMentionMode] = useState('documents'); // 'documents' | 'references' | 'manual'
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualForm, setManualForm] = useState(EMPTY_FORM);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [activeIndex, setActiveIndex] = useState(0);
  // References tab: which document groups are expanded (Set of docId/key).
  // Default: all collapsed.
  const [expandedDocs, setExpandedDocs] = useState(() => new Set());
  const startRef = useRef(null);      // textarea: char offset of "@"
  const caretAtDetectRef = useRef(null); // textarea: caret when the "@query" was last read
  const ceRangeRef = useRef(null);    // contentEditable: { node, atOffset, endOffset }
  const dropdownRef = useRef(null);
  const rootRef = useRef(null);
  const searchInputRef = useRef(null);
  const openRef = useRef(false);
  const openedAtRef = useRef(0);
  const caretRectRef = useRef({ top: 0, bottom: 0 });
  const editorRectRef = useRef({ top: 0, bottom: 99999 });
  useEffect(() => { openRef.current = open; }, [open]);

  // Resolve the target node: a textarea (direct or via selector), or the
  // contentEditable container.
  const resolveNode = useCallback(() => {
    if (textareaRef && textareaRef.current) return textareaRef.current;
    if (containerRef && containerRef.current) {
      if (contentEditable) return containerRef.current;
      if (selector) return containerRef.current.querySelector(selector);
      return containerRef.current;
    }
    return null;
  }, [textareaRef, containerRef, selector, contentEditable]);

  const docItems = useMemo(() =>
    docs.map((d) => ({
      kind: 'doc', id: d.id, label: d.name, sub: 'Document',
      // Parent collection/thread — same grouping DocumentSelector's
      // "Collections" view uses — for grouping the Documents tab.
      docId: (d.groupId != null ? d.groupId : null),
      docName: (d.groupName || null),
    })),
  [docs]);
  const nonManualRefs = useMemo(() => refs.filter(r => !r.is_manual), [refs]);
  const manualRefs = useMemo(() => refs.filter(r => r.is_manual), [refs]);

  const toItem = (r) => ({
    kind: 'ref', id: r.ar_id, label: buildRefLabel(r),
    sub: r.title ? String(r.title).slice(0, 60) : 'Reference',
    // Parent source document for grouping the References tab (from backend).
    docId: (r.doc_id != null ? r.doc_id : null),
    docName: (r.doc_name || null),
  });
  const refItems = useMemo(() => nonManualRefs.map(toItem), [nonManualRefs]);
  const manualItems = useMemo(() => manualRefs.map(toItem), [manualRefs]);
  const hasManual = manualRefs.length > 0;

  // Which tab is showing.
  const items = mentionMode === 'references' ? refItems
    : mentionMode === 'manual' ? manualItems
    : docItems;

  const effectiveQuery = searchOverride != null ? searchOverride : query;
  const isGroupedTab = mentionMode === 'references' || mentionMode === 'documents';
  const filtered = useMemo(() => {
    const q = effectiveQuery.trim().toLowerCase();
    let list = !q ? items : items.filter((it) =>
      it.label.toLowerCase().includes(q) || (it.sub && it.sub.toLowerCase().includes(q)));
    // In the References/Documents tabs, order by parent group (source document /
    // collection) so each group's items sit together under one heading. Stable
    // within a group (original order).
    if (isGroupedTab) {
      list = list
        .map((it, i) => ({ it, i }))
        .sort((a, b) => {
          const an = (a.it.docName || '\uffff');
          const bn = (b.it.docName || '\uffff');
          if (an !== bn) return an.localeCompare(bn);
          return a.i - b.i;
        })
        .map((w) => w.it);
    }
    return list.slice(0, 50);
  }, [items, effectiveQuery, isGroupedTab]);

  const isRefTab = isGroupedTab; // kept for readability at call sites below
  const docKeyOf = (it) => (it && it.docId != null ? String(it.docId) : (it && it.docName) || '__ungrouped__');
  const ungroupedLabel = mentionMode === 'documents' ? 'Other documents' : 'Other references';

  // Ordered list of groups for the References/Documents tabs.
  const docGroups = useMemo(() => {
    if (!isRefTab) return [];
    const groups = [];
    const byKey = {};
    filtered.forEach((it) => {
      const key = docKeyOf(it);
      if (!byKey[key]) {
        byKey[key] = { key, name: it.docName || ungroupedLabel, items: [] };
        groups.push(byKey[key]);
      }
      byKey[key].items.push(it);
    });
    return groups;
  }, [filtered, isRefTab, ungroupedLabel]);

  // While searching, auto-expand the groups that contain matches so results are
  // visible; when the query is cleared, collapse back to all-closed.
  const searching = effectiveQuery.trim().length > 0;
  useEffect(() => {
    if (!isRefTab) return;
    if (searching) {
      setExpandedDocs(new Set(docGroups.map((g) => g.key)));
    } else {
      setExpandedDocs(new Set());
    }
  }, [isRefTab, searching, docGroups]);

  // The items the user can actually navigate/select with the keyboard: in the
  // References tab only the refs inside expanded groups; otherwise the full list.
  const visibleItems = useMemo(() => {
    if (!isRefTab) return filtered;
    return filtered.filter((it) => expandedDocs.has(docKeyOf(it)));
  }, [isRefTab, filtered, expandedDocs]);

  const filteredRef = useRef(visibleItems);
  useEffect(() => { filteredRef.current = visibleItems; }, [visibleItems]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setSearchOverride(null);
    setMentionMode('documents');
    setManualForm(EMPTY_FORM);
    setManualError('');
    startRef.current = null;
    ceRangeRef.current = null;
    setActiveIndex(0);
    setExpandedDocs(new Set());
  }, []);

  // Raw place; final flip/clamp happens in a layout effect once the dropdown's
  // real height is known (heading + toggle + search + list vary a lot).
  const setClamped = useCallback((top, left) => {
    if (left + DROPDOWN_W > window.innerWidth) left = window.innerWidth - DROPDOWN_W - 8;
    if (left < 4) left = 4;
    setCoords({ top: top, left: left });
  }, []);

  // ── Detection: textarea ──
  const detectTextarea = useCallback((ta) => {
    const active = findActiveMention(ta.value, ta.selectionStart);
    if (!active) { close(); return; }
    const c = getTextareaCaret(ta, active.start);

    // Prefer the editor wrapper's box for left + bounds (its rect is stable;
    // the inner transparent textarea can be scrolled/offset). Fall back to the
    // textarea itself for plain textareas.
    const box = (ta.closest && ta.closest('[data-mention-editor]')) || ta;
    const brect = box.getBoundingClientRect();

    // Visible band of the editor within the viewport.
    const boxTop = Math.max(brect.top, 0);
    const boxBottom = Math.min(brect.bottom, window.innerHeight);

    // When the caret scrolls out of the editor's visible area, don't vanish and
    // don't leave the dropdown floating at a stale spot (that produced the
    // dropdown-in-the-middle-of-the-text glitch). Only close when the caret is
    // FULLY outside the visible editor band; otherwise clamp the anchor to the
    // nearest edge so the dropdown stays pinned to the editor.
    const caretBottom = c.top;
    const caretTop = c.top - c.height;
    const fullyAbove = caretBottom < boxTop;
    const fullyBelow = caretTop > boxBottom;
    if (c.outOfView && (fullyAbove || fullyBelow)) { close(); return; }

    const anchorBottom = Math.min(Math.max(caretBottom, boxTop), boxBottom);
    const anchorTop = Math.min(Math.max(caretTop, boxTop), boxBottom);

    startRef.current = active.start;
    caretAtDetectRef.current = ta.selectionStart;
    setQuery(active.query);
    setOpen(true);
    openedAtRef.current = Date.now();

    // caret line top/bottom in viewport coords (for above/below flipping),
    // clamped to the visible editor band.
    caretRectRef.current = { top: anchorTop, bottom: anchorBottom };
    // clamp the editor box to the visible viewport so the dropdown can never
    // land on the page footer, even when the editor is taller than the screen.
    editorRectRef.current = {
      top: boxTop,
      bottom: boxBottom,
    };
    // Left: anchor to the editor's left edge (small inset), not the caret x —
    // more reliable and reads naturally.
    setClamped(anchorBottom, brect.left + 8);
  }, [close, setClamped]);

  // ── Detection: contentEditable ──
  const detectContentEditable = useCallback((root) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) { close(); return; }
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE || !root.contains(node)) { close(); return; }
    if (node.parentElement?.closest(CITATION_BADGE_SELECTOR)) { close(); return; }
    const textUpto = node.textContent.slice(0, sel.anchorOffset);
    const active = findActiveMention(textUpto, textUpto.length);
    if (!active) { close(); return; }

    // Measure the caret rect via a temporary range at the "@".
    const r = document.createRange();
    r.setStart(node, active.start);
    r.setEnd(node, sel.anchorOffset);
    const rect = r.getBoundingClientRect();

    ceRangeRef.current = { node: node, atOffset: active.start, endOffset: sel.anchorOffset };
    setQuery(active.query);
    setOpen(true);
    openedAtRef.current = Date.now();
    caretRectRef.current = { top: rect.top, bottom: rect.bottom || rect.top };
    const crect = root.getBoundingClientRect();
    editorRectRef.current = {
      top: Math.max(crect.top, 0),
      bottom: Math.min(crect.bottom, window.innerHeight),
    };
    setClamped((rect.bottom || rect.top) + 2, rect.left);
  }, [close, setClamped]);

  const detect = useCallback(() => {
    const node = resolveNode();
    if (!node) return;
    if (contentEditable) detectContentEditable(node);
    else detectTextarea(node);
  }, [resolveNode, contentEditable, detectTextarea, detectContentEditable]);

  // ── Insert: textarea ──
  const insertTextarea = useCallback((ta, item) => {
    const start = startRef.current;
    if (start == null) return;
    // The caret always sits after the "@". When the textarea has lost its selection
    // by the time an item is clicked (seen in portalled popups: it reads 0), the
    // whole value would land after the chip as a duplicate, so fall back to the
    // caret recorded when the mention was detected.
    let caret = ta.selectionStart;
    if (caret == null || caret <= start) caret = caretAtDetectRef.current ?? start + 1;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(caret);
    const html = buildCitationHTML(item);
    // No trailing space: the span already carries mx-1 (horizontal margin), so
    // an extra space here would double the gap vs. backend-rendered citations.
    const newValue = before + html + after;
    const newCaret = (before + html).length;
    if (onInsert) onInsert(newValue, newCaret);
  }, [onInsert]);

  // ── Insert: contentEditable ──
  const insertContentEditable = useCallback((root, item) => {
    const info = ceRangeRef.current;
    if (!info || !info.node || !root.contains(info.node)) return;
    // Replace the "@query" text with the citation span node.
    const r = document.createRange();
    const previousNode = info.node.previousSibling;
    const startsAfterCitationAnchor =
      info.atOffset > 0 &&
      info.node.textContent[info.atOffset - 1] === '\u200B' &&
      previousNode?.nodeType === Node.ELEMENT_NODE &&
      previousNode.matches(CITATION_BADGE_SELECTOR);
    r.setStart(info.node, startsAfterCitationAnchor ? info.atOffset - 1 : info.atOffset);
    r.setEnd(info.node, info.endOffset);
    r.deleteContents();

    // Insert the citation span followed by a zero-width space text node (no
    // trailing nbsp — the span's mx-1 already provides spacing; an nbsp would
    // double the gap). This anchor is kept permanently (not removed) — without
    // a real node marking "outside the span", typing right after an inline
    // span is ambiguous in contentEditable, and some browsers resolve that by
    // inserting new content INSIDE the previous span instead of after it.
    // That's what produced nested citation pills when adding a second
    // citation right after the first. Keeping this node gives every future
    // "@" detection and insertion a real text node to anchor to, outside the
    // span, so citations always land as siblings, never nested.
    const tmp = document.createElement('div');
    tmp.innerHTML = buildCitationHTML(item);
    const frag = document.createDocumentFragment();
    let spanNode = null;
    while (tmp.firstChild) {
      spanNode = tmp.firstChild;
      if (spanNode.nodeType === Node.ELEMENT_NODE && spanNode.matches(CITATION_BADGE_SELECTOR)) {
        spanNode.setAttribute('contenteditable', 'false');
      }
      frag.appendChild(spanNode);
    }
    const caretAnchor = document.createTextNode('\u200B'); // zero-width space
    frag.appendChild(caretAnchor);
    r.insertNode(frag);

    // Place the caret inside the anchor (after its one character) — outside
    // the span, inside a real, persistent text node.
    const sel = window.getSelection();
    const after = document.createRange();
    after.setStart(caretAnchor, 1);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);

    // Fire input so the host (TableComponent) captures the new innerHTML.
    root.dispatchEvent(new Event('input', { bubbles: true }));
    if (onInsert) onInsert();
  }, [onInsert]);

  const insertItem = useCallback((item) => {
    if (typeof onSelectItem === 'function') {
      onSelectItem(item, { query });
      close();
      return;
    }
    const node = resolveNode();
    if (!node || !item) return;
    if (contentEditable) insertContentEditable(node, item);
    else insertTextarea(node, item);
    close();
  }, [resolveNode, contentEditable, insertContentEditable, insertTextarea, close, onSelectItem, query]);
  const insertRef = useRef(insertItem);
  useEffect(() => { insertRef.current = insertItem; }, [insertItem]);

  const toggleDoc = useCallback((key) => {
    setExpandedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
    setActiveIndex(0);
  }, []);

  // ── Bind listeners (rebind if the node changes; textarea may mount late) ──
  useEffect(() => {
    let boundNode = null;
    let timer = null;

    const onDetect = () => detect();
    const onKeyDown = (e) => {
      if (!openRef.current) return;
      const list = filteredRef.current;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, list.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
      else if (e.key === 'Enter' || e.key === 'Tab') {
        if (list.length > 0) { e.preventDefault(); setActiveIndex((i) => { insertRef.current(list[i] || list[0]); return i; }); }
      } else if (e.key === 'Escape') { e.preventDefault(); close(); }
    };

    const bind = (node) => {
      node.addEventListener('keyup', onDetect);
      node.addEventListener('click', onDetect);
      node.addEventListener('input', onDetect);
      node.addEventListener('keydown', onKeyDown);
    };
    const unbind = (node) => {
      node.removeEventListener('keyup', onDetect);
      node.removeEventListener('click', onDetect);
      node.removeEventListener('input', onDetect);
      node.removeEventListener('keydown', onKeyDown);
    };

    const tick = () => {
      const node = resolveNode();
      if (node && node !== boundNode) {
        if (boundNode) unbind(boundNode);
        bind(node);
        boundNode = node;
      }
      timer = window.setTimeout(tick, 300);
    };
    tick();

    return () => {
      if (timer) window.clearTimeout(timer);
      if (boundNode) unbind(boundNode);
    };
  }, [resolveNode, detect, close]);

  // ── Reposition on editor/page scroll; close only on genuine outside scroll ──
  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => {
      if (Date.now() - openedAtRef.current < 250) return;
      // Scroll inside the dropdown's own list — ignore.
      if (rootRef.current && e.target && rootRef.current.contains(e.target)) return;

      const node = resolveNode();
      const container = containerRef && containerRef.current;
      const tgt = e.target;
      const isEl = tgt && tgt.nodeType === 1;

      // Scroll came from the editor (its textarea/preview/container) or from the
      // document/window itself → the caret moved relative to the viewport, so
      // re-measure and re-place. detect() closes itself if the caret has scrolled
      // fully out of the editor's visible band.
      const insideEditor =
        !isEl ||                                                            // document/window scroll
        (node && node.contains && (tgt === node || node.contains(tgt))) ||
        (container && container.contains && (tgt === container || container.contains(tgt)));

      if (insideEditor) { detect(); return; }
      // Some other scroll container on the page → close.
      close();
    };
    const onResize = () => close();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close, detect, resolveNode, containerRef]);

  // ── Close on click / focus outside the editor and the dropdown ──
  useEffect(() => {
    if (!open) return undefined;
    const isOutside = (target) => {
      if (!target) return true;
      if (rootRef.current && rootRef.current.contains(target)) return false;
      // Treat the whole editor container (and its resolved textarea) as inside,
      // so focus moving between editor internals doesn't close us.
      const container = containerRef && containerRef.current;
      if (container && container.contains && container.contains(target)) return false;
      const node = resolveNode();
      if (node && node.contains && node.contains(target)) return false;
      return true;
    };
    const onDown = (e) => { if (isOutside(e.target)) close(); };
    const onFocusIn = (e) => {
      // Ignore the focus churn that happens right as the dropdown opens.
      if (Date.now() - openedAtRef.current < 300) return;
      if (isOutside(e.target)) close();
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
    };
  }, [open, close, resolveNode, containerRef]);

  useEffect(() => {
    if (!open || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector('[data-idx="' + activeIndex + '"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  // Once the dropdown is in the DOM, measure its REAL height and decide whether
  // it fits below the caret; if not, flip it above. Then clamp to the viewport
  // so it's never cut off at the top or bottom. Runs on open and whenever the
  // content (mode/query/filtered length) changes the height.
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const el = rootRef.current;
    const h = el.offsetHeight;
    const margin = 6;
    // Keep clear of the browser/OS chrome (taskbar, status bar) at the very
    // bottom of the window — the raw viewport height overlaps it.
    const SAFE_BOTTOM = 48;
    const caret = caretRectRef.current;
    const editor = editorRectRef.current;

    // Usable band: inside the editor box, inside the viewport, and above the
    // bottom safe zone.
    const lowerBound = Math.min(window.innerHeight - SAFE_BOTTOM, editor.bottom) - margin;
    const upperBound = Math.max(0, editor.top) + margin;

    const spaceBelow = lowerBound - caret.bottom;
    const spaceAbove = caret.top - upperBound;

    // Decide the flip direction (above vs below the caret) using a FIXED
    // height estimate (DROPDOWN_H), not the dropdown's real, content-dependent
    // height. The real height changes on every keystroke as the filtered list
    // grows/shrinks — deciding direction from it made the dropdown visibly
    // flip up/down mid-typing whenever that height crossed the fit threshold.
    // The estimate only depends on caret position + available space, both of
    // which stay stable while typing on the same line, so the direction no
    // longer flickers. The actual height (h) is still used below to clamp so
    // nothing overflows off-screen or past the editor bounds.
    const openBelow = spaceBelow >= DROPDOWN_H || spaceBelow >= spaceAbove;
    let top = openBelow ? (caret.bottom + 2) : (caret.top - h - 2);

    // Final hard clamp to the usable band (viewport ∩ editor).
    if (top + h > lowerBound) top = lowerBound - h;
    if (top < upperBound) top = upperBound;

    setCoords((c) => (Math.abs(c.top - top) > 1 ? { top: top, left: c.left } : c));
  }, [open, mentionMode, query, searchOverride, filtered.length, visibleItems.length, docGroups.length, expandedDocs]);

  if (!open) return null;

  const positionStyle = {
    '--mention-top': Math.round(coords.top) + 'px',
    '--mention-left': Math.round(coords.left) + 'px',
  };

  const switchMode = (mode) => {
    setMentionMode(mode);
    setShowManualForm(false);
    setSearchOverride(null);
    setQuery('');
    setActiveIndex(0);
    setManualError('');
    // References + Manual both come from the same lazy fetch.
    if ((mode === 'references' || mode === 'manual') && typeof onNeedReferences === 'function') {
      onNeedReferences();
    }
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  const submitManual = async () => {
    if (typeof onCreateReference !== 'function' || manualSaving) return;
    if (!manualForm.title.trim() && !manualForm.authors.trim()) {
      setManualError('Enter at least a title or an author.');
      return;
    }
    setManualError('');
    setManualSaving(true);
    try {
      const created = await onCreateReference({
        classification: manualForm.classification,
        authors: manualForm.authors,
        year: manualForm.year,
        title: manualForm.title,
        journal_page_no: manualForm.journal_page_no,
        publisher: manualForm.publisher,
        organization: manualForm.organization,
        doi: manualForm.doi,
        website_url: manualForm.website_url,
        accessed_date: manualForm.accessed_date,
      });
      if (created && created.ar_id != null) {
        // Insert the freshly created reference straight into the text.
        insertItem({
          kind: 'ref', id: created.ar_id, label: buildRefLabel(created),
          sub: created.title ? String(created.title).slice(0, 60) : 'Reference',
        });
      } else {
        // Kept open: reset form, go back to the Manual tab list.
        setManualForm(EMPTY_FORM);
        setShowManualForm(false);
        setMentionMode('manual');
      }
    } catch (e) {
      setManualError('Could not save the reference. Try again.');
    } finally {
      setManualSaving(false);
    }
  };

  // Heading + Documents/References toggle (same shell as DocumentSelector's
  // ev-view-toggle). References are lazy-fetched on first open.
  const headerBar = (
    <div className="genbi-mention-header d-flex align-items-center justify-content-between mb-2 px-1">
      <span className="ev-reg-p-blue"> citation</span>
      <div className="ev-view-toggle-shell">
        <button type="button"
          className={'ev-view-toggle-btn ' + (mentionMode === 'documents' ? 'active' : '')}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); switchMode('documents'); }}>
          Documents
        </button>
        <button type="button"
          className={'ev-view-toggle-btn ' + (mentionMode === 'references' ? 'active' : '')}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); switchMode('references'); }}>
          References
        </button>
        {(hasManual || mentionMode === 'manual') && (
          <button type="button"
            className={'ev-view-toggle-btn ' + (mentionMode === 'manual' ? 'active' : '')}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); switchMode('manual'); }}>
            Manual
          </button>
        )}
      </div>
    </div>
  );

  // Same search markup as AgentConfigure (form-control search-input +
  // ev-btn-lightblue-fill-round search-bar-button). Filtering is live here.
  const searchHeader = (
    <div className="genbi-mention-searchwrap position-relative mb-2 px-1">
      <input
        ref={searchInputRef}
        type="text"
        className="form-control search-input ms-0"
        placeholder={mentionMode === 'references' ? 'Search references by author' : 'Search documents by name'}
        value={effectiveQuery}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => { setSearchOverride(e.target.value); setActiveIndex(0); }}
        onKeyDown={(e) => {
          const list = filteredRef.current;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex((i) => Math.min(i + 1, list.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex((i) => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (list.length) insertItem(list[activeIndex] || list[0]); }
          else if (e.key === 'Escape') { e.preventDefault(); close(); }
        }}
      />
      <button type="button" className="ev-btn-lightblue-fill-round search-bar-button" tabIndex={-1}>
        <i className="bi bi-search"></i>
      </button>
    </div>
  );

  // Manual "add reference" form (DB-backed fields only). Shown when the user
  // taps the + in the References view.
  const field = (label, key, opts = {}) => (
    <div className="genbi-mref-row d-flex mb-2">
      <label className="genbi-mref-label ev-fs-11">{label}</label>
      {opts.select ? (
        <select className="form-control search-input ms-0 genbi-mref-input"
          value={manualForm[key]}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => setManualForm(f => ({ ...f, [key]: e.target.value }))}>
          {REF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      ) : (
        <input type="text"
          className="form-control search-input ms-0 genbi-mref-input"
          placeholder={opts.placeholder || 'Type here…'}
          value={manualForm[key]}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => setManualForm(f => ({ ...f, [key]: e.target.value }))} />
      )}
    </div>
  );

  const manualHeader = (
    <div className="genbi-mention-header d-flex align-items-center justify-content-between mb-2 px-1">
      <span className="ev-reg-p-blue">Add Citation</span>
      <button type="button"
        className="genbi-mref-back border-0 bg-transparent ev-fs-11 d-flex align-items-center gap-1"
        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setShowManualForm(false); setManualError(''); }}>
        <i className="bi bi-arrow-left"></i> back
      </button>
    </div>
  );

  const manualView = (
    <div ref={rootRef} className="genbi-mention-dropdown ev-card-4 d-flex flex-column" style={positionStyle}
      onMouseDown={(e) => e.preventDefault()}>
      {manualHeader}
      <div className="genbi-mref-form">
        {field('Reference Type', 'classification', { select: true })}
        {field('Author', 'authors', { placeholder: 'Comma-separated, e.g. Smith J, Doe A' })}
        {field('Year', 'year')}
        {field('Title', 'title')}
        {field('Journal / Pages', 'journal_page_no')}
        {field('Publisher', 'publisher')}
        {field('Organization', 'organization')}
        {field('DOI', 'doi')}
        {field('Website URL', 'website_url', { placeholder: 'https://example.com/page' })}
        {field('Accessed Date', 'accessed_date', { placeholder: 'e.g. 20 June 2024' })}
        {manualError && <div className="genbi-mref-error ev-fs-11 mb-2">{manualError}</div>}
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="ev-btn-white-outline-round ev-btn-xsmall"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (!manualSaving) { setShowManualForm(false); setManualError(''); } }}
            disabled={manualSaving}>
            Cancel
          </button>
          <button type="button" className="ev-btn-blue-fill-round ev-btn-xsmall"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); if (!manualSaving) submitManual(); }}
            disabled={manualSaving}>
            {manualSaving ? 'Saving…' : 'Add reference'}
          </button>
        </div>
      </div>
    </div>
  );

  if (mentionMode === 'manual' && showManualForm) {
    return createPortal(manualView, document.body);
  }

  const emptyText = ((mentionMode === 'references' || mentionMode === 'manual') && refsLoading)
    ? 'Loading references…'
    : items.length === 0
      ? (mentionMode === 'references' ? 'No references available for this agent'
        : mentionMode === 'manual' ? 'No manual references yet — tap + to add one'
        : 'No documents configured for this agent')
      : 'No matches';

  // "Add reference manually" — inline row at the bottom of the list (not a
  // floating button, so it never overlaps list content).
  const addRefRow = ((mentionMode === 'references' || mentionMode === 'manual') && typeof onCreateReference === 'function') ? (
    <button type="button"
      className="genbi-mention-add-row d-flex align-items-center gap-2 border-0 bg-transparent text-start w-100 px-2 py-2"
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setMentionMode('manual'); setShowManualForm(true); setManualError(''); }}>
      <i className="bi bi-plus-lg"></i>
      <span className="ev-fs-12 ev-bold-med">Add reference manually</span>
    </button>
  ) : null;

  if (filtered.length === 0) {
    return createPortal(
      <div ref={rootRef} className="genbi-mention-dropdown ev-card-4 d-flex flex-column position-relative" style={positionStyle}
        onMouseDown={(e) => e.preventDefault()}>
        {headerBar}
        {searchHeader}
        <div className="genbi-mention-empty ev-fs-11">{emptyText}</div>
        {addRefRow}
      </div>,
      document.body
    );
  }

  return createPortal(
    <div ref={rootRef} className="genbi-mention-dropdown ev-card-4 d-flex flex-column position-relative" style={positionStyle}
      onMouseDown={(e) => e.preventDefault()}>
      {headerBar}
      {searchHeader}
      <div ref={dropdownRef} className="genbi-mention-list d-flex flex-column">
        {isRefTab ? (
          docGroups.map((g) => {
            const isOpen = expandedDocs.has(g.key);
            return (
              <div key={'grp-' + g.key} className="genbi-mention-group">
                <button type="button"
                  className="genbi-mention-doc-toggle d-flex align-items-center gap-2 border-0 bg-transparent text-start w-100 px-2 py-1"
                  aria-expanded={isOpen}
                  onClick={() => toggleDoc(g.key)}>
                  <i className={'bi ' + (isOpen ? 'bi-chevron-down' : 'bi-chevron-right') + ' genbi-mention-chev'}></i>
                  <i className="bi bi-file-earmark-text"></i>
                  <span className="genbi-mention-doc-name ev-fs-12 ev-bold-med text-truncate flex-grow-1"
                    title={g.name}>{g.name}</span>
                  <span className="genbi-mention-doc-count ev-fs-11">{g.items.length}</span>
                </button>
                {isOpen && (
                  <div className="genbi-mention-group-body">
                    {g.items.map((it) => {
                      const vIdx = visibleItems.indexOf(it);
                      return (
                        <button key={it.kind + '-' + it.id + '-' + g.key} type="button" data-idx={vIdx}
                          className={'genbi-mention-item d-flex align-items-center gap-2 border-0 bg-transparent text-start w-100 px-2 py-1 ' + (vIdx === activeIndex ? 'active' : '')}
                          onMouseEnter={() => setActiveIndex(vIdx)}
                          onClick={() => insertItem(it)}>
                          <i className={'bi ' + (it.kind === 'ref' ? 'bi-journal-text' : 'bi-file-earmark-text')}></i>
                          <span className="d-flex flex-column overflow-hidden">
                            <span className="genbi-mention-label ev-fs-12 ev-bold-med text-truncate">{it.label}</span>
                            <span className="genbi-mention-sub ev-fs-11 text-truncate">{it.sub}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          filtered.map((it, idx) => (
            <button key={it.kind + '-' + it.id} type="button" data-idx={idx}
              className={'genbi-mention-item d-flex align-items-center gap-2 border-0 bg-transparent text-start w-100 px-2 py-1 ' + (idx === activeIndex ? 'active' : '')}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => insertItem(it)}>
              <i className={'bi ' + (it.kind === 'ref' ? 'bi-journal-text' : 'bi-file-earmark-text')}></i>
              <span className="d-flex flex-column overflow-hidden">
                <span className="genbi-mention-label ev-fs-12 ev-bold-med text-truncate">{it.label}</span>
                <span className="genbi-mention-sub ev-fs-11 text-truncate">{it.sub}</span>
              </span>
            </button>
          ))
        )}
        {addRefRow}
      </div>
    </div>,
    document.body
  );
};

export default MentionAutocomplete;
